import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const fmtPct = (v) => (v == null ? "N/A" : `${v.toFixed(1)}%`);

// Build 8 — lightweight read-only Group Overview: legal entities side-by-side.
// Not the final management dashboard (out of scope, §45/§71).
export default function GroupOverviewPage() {
  const { year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noGroup, setNoGroup] = useState(false);

  useEffect(() => {
    // discover years via the hub's financial years
    api.get("/companies")
      .then(async (res) => {
        const hub = res.data.find((c) => c.is_group_company);
        if (!hub) { setNoGroup(true); return; }
        const yrs = await api.get(`/companies/${hub.id}/periods/years`);
        setYears(yrs.data);
        const pick = pickActiveYear(yrs.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!year) return;
    setLoading(true);
    api.get("/group/overview", { params: { year } })
      .then((res) => setData(res.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <div data-testid="group-overview-page">
      <PageHeader
        title="Group Overview"
        subtitle="Legal entities side-by-side — standalone legal-entity economics, no eliminations"
        actions={years.length > 0 && (
          <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="go-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`go-year-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      />
      {noGroup && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <EmptyState title="No Group company configured" description="Mark a company as the Group company under Administration → Companies." testId="go-no-group" />
        </div>
      )}
      {loading && <div className="py-16 text-center text-sm text-slate-400" data-testid="go-loading">Loading group overview…</div>}
      {!loading && data && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="go-table">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                {["Company", `Sales ${data.year}`, "PBT", "Net Margin", "Closing Cash", "Eligible Consultants (Dec)", "Group Overhead FY"].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.companies.map((c) => (
                <tr key={c.company_id} className="border-b border-slate-100 hover:bg-accent/40" data-testid={`go-row-${c.company_id}`}>
                  <td className="py-3 px-4">
                    <span className="text-sm font-semibold text-slate-800">{c.company_name}</span>
                    {c.is_hub && <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#0047B3] border border-[#CFE3FC]">Hub</span>}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-800">{fmtSEK(c.sales)} kr</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={c.pbt < 0 ? "text-[#B91C1C] font-semibold" : "text-slate-800"}>{fmtSEK(c.pbt)} kr</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{fmtPct(c.net_margin)}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={c.closing_cash < 0 ? "text-[#B91C1C] font-semibold" : "text-slate-800"}>{fmtSEK(c.closing_cash)} kr</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-800">{c.eligible_headcount_dec}</td>
                  <td className="py-3 px-4 text-sm text-slate-800" data-testid={`go-oh-${c.company_id}`}>
                    {fmtSEK(c.group_overhead_fy)} kr
                    {c.is_hub && <span className="block text-[10px] text-slate-400">retained share</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
